// /client/src/components/BalanceView.js
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import Footer from './Footer';
import PageSEO from './PageSEO';
import './BalanceView.css';

const BalanceView = () => {

  useEffect(() => {
    window.scrollTo(0, 0);

    // Ensure the container exists before loading the script
    const loadScript = () => {
      const container = document.getElementById('katana-widget-container');
      if (!container) {
        console.error('Katana widget container not found');
        return;
      }

      // Set up a MutationObserver to watch for when Katana adds content to body
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            // Check if this is a Katana widget element
            if (node.nodeType === 1) { // Element node
              const element = node;
              // Look for Angular ng-scope class or ui-view attribute
              if (element.classList?.contains('ng-scope') || 
                  element.hasAttribute?.('ui-view') ||
                  element.querySelector?.('[ui-view]') ||
                  element.querySelector?.('.ng-scope')) {
                
                // Move it into our container
                const targetContainer = document.getElementById('katana-widget-container');
                if (targetContainer && !targetContainer.contains(element)) {
                  
                  targetContainer.appendChild(element);
                  // Stop observing once we've moved it
                  observer.disconnect();
                }
              }
            }
          });
        });
      });

      // Start observing the body for child additions
      observer.observe(document.body, {
        childList: true,
        subtree: false
      });

      // Load the Katana gift card balance widget script
      const script = document.createElement('script');
      script.src = 'https://api2.chockstone.net:443/katana/v1/widgets/scripts/bootstrap_katana_account_dist.js?katanaDomain=hps&katanaChain=250825064817604&katanaApiKey=3ddbgq2czhfc3';
      script.async = true;
      script.id = 'katana-balance-script';
      
      // Append to body (scripts need to be in document to execute)
      document.body.appendChild(script);

      // Store observer for cleanup
      return observer;
    };

    // Small delay to ensure DOM is fully ready
    const timer = setTimeout(() => {
      const observer = loadScript();
      
      // Cleanup observer after 10 seconds if widget hasn't loaded
      const cleanupTimer = setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 10000);

      // Store cleanup timer
      window.katanaCleanupTimer = cleanupTimer;
    }, 100);
    
    // Cleanup function to remove script when component unmounts
    return () => {
      clearTimeout(timer);
      if (window.katanaCleanupTimer) {
        clearTimeout(window.katanaCleanupTimer);
      }
      const existingScript = document.getElementById('katana-balance-script');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  // Comprehensive SEO configuration for gift card balance page
  const balanceSEO = {
    title: "Check Gift Card Balance | Kinder's Meats Deli BBQ & Catering",
    description: "Check your Kinder's gift card balance online instantly. Look up your gift card balance and see available funds for use at any of our 7 California BBQ restaurant locations.",
    keywords: [
      "Kinder's gift card balance",
      "check gift card balance",
      "gift card balance lookup",
      "BBQ gift card balance",
      "restaurant gift card balance",
      "Kinder's gift card",
      "gift card balance check",
      "check card balance online",
      "California restaurant gift card",
      "BBQ restaurant gift card",
      "gift card balance inquiry",
      "gift card funds",
      "restaurant gift card lookup",
      "Kinder's card balance",
      "gift card balance checker"
    ],
    canonicalUrl: "/balance",
    ogImage: "https://kindersmeats.com/images/gift-card-balance.webp",
    customMeta: {
      'application-name': "Kinder's Gift Card Balance",
      'apple-mobile-web-app-title': "Card Balance",
      'format-detection': 'telephone=no'
    },
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://kindersmeats.com/balance#webpage",
          "url": "https://kindersmeats.com/balance",
          "name": "Check Gift Card Balance | Kinder's Meats Deli BBQ & Catering",
          "description": "Check your Kinder's gift card balance online instantly. Look up your gift card balance and see available funds for use at any of our 7 California BBQ restaurant locations.",
          "isPartOf": {
            "@id": "https://kindersmeats.com/#website"
          },
          "about": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "breadcrumb": {
            "@id": "https://kindersmeats.com/balance#breadcrumb"
          },
          "potentialAction": [
            {
              "@type": "CheckAction",
              "name": "Check Gift Card Balance",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": "https://kindersmeats.com/balance",
                "actionPlatform": [
                  "http://schema.org/DesktopWebPlatform",
                  "http://schema.org/MobileWebPlatform"
                ]
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://kindersmeats.com/#organization",
          "name": "Kinder's Meats Deli BBQ & Catering",
          "url": "https://kindersmeats.com",
          "logo": {
            "@type": "ImageObject",
            "url": "https://kindersmeats.com/images/kinders-logo.webp"
          },
          "sameAs": [
            "https://www.facebook.com/kindersmeats",
            "https://www.instagram.com/kindersmeats"
          ]
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://kindersmeats.com/balance#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://kindersmeats.com"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Gift Card Balance",
              "item": "https://kindersmeats.com/balance"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://kindersmeats.com/balance#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How do I check my Kinder's gift card balance?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "You can check your Kinder's gift card balance online by entering your gift card number in the balance lookup tool on this page. Your current balance will be displayed instantly."
              }
            },
            {
              "@type": "Question",
              "name": "What information do I need to check my balance?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "You'll need your gift card number, which can be found on the front or back of your Kinder's gift card."
              }
            },
            {
              "@type": "Question",
              "name": "Where can I use my Kinder's gift card?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Kinder's gift cards can be used at any of our 7 California locations in Brentwood, Concord, Hercules, Martinez, Pleasant Hill, and both Walnut Creek locations."
              }
            },
            {
              "@type": "Question",
              "name": "Can I check my balance in person?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, you can also check your gift card balance by visiting any Kinder's location or calling your nearest restaurant."
              }
            }
          ]
        },
        {
          "@type": "Product",
          "@id": "https://kindersmeats.com/balance#giftcard",
          "name": "Kinder's Gift Card",
          "description": "Gift cards for Kinder's Meats Deli BBQ & Catering, redeemable at all California locations",
          "brand": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "offers": {
            "@type": "Offer",
            "availability": "https://schema.org/InStock",
            "priceCurrency": "USD",
            "url": "https://kindersmeats.com/giftcards"
          }
        }
      ]
    }
  };

  return (
    <>
      {/* SEO Component */}
      <PageSEO {...balanceSEO} />
      
      {/* Main content with semantic HTML structure */}
      <main>
        <section className="theme-light section m-0 p-0" data-theme="light">
          <div className='is-flex is-flex-direction-column is-justify-content-space-between fullHeight'>
            <Navigation 
              colorMode="colorWhite"
            />
            
            {/* Gift Card Balance Content */}
            <article itemScope itemType="https://schema.org/WebPage">
              <div className="column content-space-160 is-flex is-flex-direction-column is-10-mobile is-10-tablet is-10-desktop is-10-widescreen is-10-fullhd is-offset-1-mobile is-offset-1-tablet is-offset-1-desktop is-offset-1-widescreen is-offset-1-fullhd">
                <div className="m-0 p-0 columns is-flex is-justify-content-center">
                  {/* Katana widget will render here */}
                  <div id="katana-widget-container"></div>
                </div>
              </div>
            </article>
            
            <Footer />
          </div>
        </section>
      </main>
    </>
  );
};

export default BalanceView;